import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Trash2, ChevronLeft, ChevronRight, Tag, 
  TrendingUp, TrendingDown, Minus, AlertTriangle, 
  ArrowUpRight, ArrowDownRight, Lightbulb, Filter
} from "lucide-react";
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Cell,
  ComposedChart, Area
} from "recharts";

const CATEGORY_INFO = {
  I: { label: "Indispensabili", color: "#3B82F6", bgColor: "bg-blue-500", lightBg: "bg-blue-50", textColor: "text-blue-700" },
  A: { label: "Accessorie", color: "#8B5CF6", bgColor: "bg-purple-500", lightBg: "bg-purple-50", textColor: "text-purple-700" },
  X: { label: "Imprevisti", color: "#EF4444", bgColor: "bg-red-500", lightBg: "bg-red-50", textColor: "text-red-700" }
};

export const ExpensesSummaryCards = ({ totals, showPercentages, getDelta }) => {
  const delta = getDelta(totals.current.total, totals.previous.total);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Card className="border shadow-sm col-span-2 sm:col-span-1">
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground mb-1">Totale</p>
          <p className="text-xl font-bold font-mono">{"\u20AC"}{totals.current.total.toFixed(2)}</p>
          {delta.percent !== null && (
            <div className={`flex items-center text-xs mt-1 ${delta.status === "up" ? "text-red-600" : delta.status === "down" ? "text-green-600" : "text-gray-500"}`}>
              {delta.status === "up" ? <ArrowUpRight className="w-3 h-3" /> : delta.status === "down" ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
              <span>{delta.percent?.toFixed(0)}%</span>
            </div>
          )}
        </CardContent>
      </Card>
      {["I", "A", "X"].map(cat => (
        <Card key={cat} className={`border shadow-sm`}>
          <CardContent className="p-3">
            <p className={`text-xs mb-1 ${CATEGORY_INFO[cat].textColor}`}>{CATEGORY_INFO[cat].label}</p>
            <p className="text-lg font-bold font-mono">
              {showPercentages && totals.current.total > 0 
                ? `${((totals.current[cat] / totals.current.total) * 100).toFixed(0)}%`
                : `\u20AC${totals.current[cat].toFixed(0)}`
              }
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const ExpensesCharts = ({ chartData, totals, previousRange, topSubcategories, getDelta, showPercentages, formatXAxis, CustomTooltip }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {/* Andamento nel tempo */}
    <Card className="border shadow-sm">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-1">
          <TrendingUp className="w-4 h-4" /> Andamento
        </CardTitle>
      </CardHeader>
      <CardContent className="px-1 pb-3">
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="total" fill="#6366f1" fillOpacity={0.1} stroke="none" name="Totale" />
            <Line type="monotone" dataKey="total" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} name="Totale" />
            <Line type="monotone" dataKey="movingAvg" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="Media mobile" />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    {/* Macro per giorno */}
    <Card className="border shadow-sm">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-1">
          <Filter className="w-4 h-4" /> Per categoria
        </CardTitle>
      </CardHeader>
      <CardContent className="px-1 pb-3">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="I" stackId="a" fill={CATEGORY_INFO.I.color} name="Indispensabili" />
            <Bar dataKey="A" stackId="a" fill={CATEGORY_INFO.A.color} name="Accessorie" />
            <Bar dataKey="X" stackId="a" fill={CATEGORY_INFO.X.color} name="Imprevisti" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    {/* Confronto periodo precedente */}
    <Card className="border shadow-sm">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-1">
          <TrendingDown className="w-4 h-4" /> Confronto
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="space-y-3">
          {["total", "I", "A", "X"].map(key => {
            const label = key === "total" ? "Totale" : CATEGORY_INFO[key]?.label;
            const d = getDelta(totals.current[key], totals.previous[key]);
            return (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{"\u20AC"}{totals.current[key].toFixed(0)}</span>
                  {d.percent !== null && (
                    <Badge variant={d.status === "down" ? "outline" : "destructive"} className="text-xs">
                      {d.status === "up" ? "+" : ""}{d.percent?.toFixed(0)}%
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>

    {/* Top Sottocategorie */}
    <Card className="border shadow-sm">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-1">
          <Tag className="w-4 h-4" /> Top sottocategorie
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="space-y-2">
          {topSubcategories.slice(0, 6).map((sc, i) => {
            const maxAmount = topSubcategories[0]?.amount || 1;
            return (
              <div key={sc.name} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate flex-1">{sc.name}</span>
                  <span className="font-mono ml-2">{"\u20AC"}{sc.amount.toFixed(0)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${(sc.amount / maxAmount) * 100}%`, backgroundColor: CATEGORY_INFO[sc.category]?.color || "#6366f1" }}
                  />
                </div>
              </div>
            );
          })}
          {topSubcategories.length === 0 && <p className="text-xs text-muted-foreground">Nessuna spesa nel periodo</p>}
        </div>
      </CardContent>
    </Card>
  </div>
);

export const ExpensesInsights = ({ insights, bigImprevisti, imprevistThreshold }) => (
  <>
    {bigImprevisti.length > 0 && (
      <Card className="border border-red-200 bg-red-50 shadow-sm">
        <CardContent className="p-3">
          <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" /> Imprevisti grandi ({"\u20AC"}{imprevistThreshold}+)
          </h3>
          <div className="space-y-1">
            {bigImprevisti.map(e => (
              <div key={e.expense_id} className="flex justify-between text-sm">
                <span className="text-red-800">{e.description || e.subcategory}</span>
                <span className="font-mono font-semibold text-red-700">{"\u20AC"}{e.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    {insights.length > 0 && (
      <Card className="border shadow-sm bg-amber-50/50">
        <CardContent className="p-3">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-600" /> Insight
          </h3>
          <ul className="space-y-1">
            {insights.map((insight, i) => (
              <li key={`insight-${insight.substring(0, 20)}-${i}`} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="text-amber-500 mt-1">{"\u2022"}</span> {insight}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    )}
  </>
);

export const ExpensesList = ({ filteredExpenses, onDelete }) => (
  <Card className="border shadow-sm">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">Movimenti ({filteredExpenses.length})</CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <div className="max-h-64 overflow-y-auto">
        {filteredExpenses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Nessuna spesa nel periodo</p>
        ) : (
          filteredExpenses.sort((a, b) => b.date.localeCompare(a.date)).map(exp => (
            <div key={exp.expense_id} className="flex items-center justify-between px-3 py-2 border-t first:border-t-0">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`w-2 h-2 rounded-full ${CATEGORY_INFO[exp.category]?.bgColor}`} />
                <div className="min-w-0">
                  <p className="text-sm truncate">{exp.description || exp.subcategory || "Spesa"}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(exp.date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                    {exp.subcategory && ` \u00B7 ${exp.subcategory}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold">{"\u20AC"}{exp.amount.toFixed(2)}</span>
                <button onClick={() => onDelete(exp.expense_id)} className="p-1 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </CardContent>
  </Card>
);
